import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { operationBlocksApi } from '../../features/operation-blocks/api';
import { getErrorMessage } from '../../shared/api/client';

const normalizeApiErrorDetails = (details) => {
  if (!details) {
    return null;
  }

  return {
    active: true,
    type: details.reason,
    mode: details.mode,
    title: details.title,
    message: details.description,
    supportMessage: details.supportMessage,
    startsAt: details.startsAt,
    endsAt: details.endsAt,
    retryAfterSeconds: details.retryAfterSeconds,
    allowedActions: details.allowedActions || [],
    allowBypass: details.allowBypass,
    bypassRoles: details.bypassRoles,
    bypassActive: details.bypassActive,
    originalMode: details.originalMode,
    bypassExpiresAt: details.bypassExpiresAt,
  };
};

export const fetchAppConfig = createAsyncThunk(
  'operationBlock/fetchAppConfig',
  async ({ surface = 'WEB_ADMIN' } = {}, { rejectWithValue }) => {
    try {
      const response = await operationBlocksApi.getAppConfig(surface);
      return response.data?.data?.operationBlock || { active: false };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const refreshOperationBlockStatus = fetchAppConfig;

const initialState = {
  status: 'unknown',
  block: null,
  lastCheckedAt: null,
  source: null,
  error: null,
};

const operationBlockSlice = createSlice({
  name: 'operationBlock',
  initialState,
  reducers: {
    setOperationBlockFromApiError: (state, action) => {
      const block = normalizeApiErrorDetails(action.payload);
      if (!block) {
        return;
      }
      state.status = 'blocked';
      state.block = {
        ...(state.block || {}),
        ...block,
        bypassRoles: block.bypassRoles || state.block?.bypassRoles,
        allowBypass: block.allowBypass !== undefined ? block.allowBypass : state.block?.allowBypass,
        supportMessage: block.supportMessage || state.block?.supportMessage,
        bypassActive: block.bypassActive !== undefined ? block.bypassActive : state.block?.bypassActive,
        originalMode: block.originalMode || state.block?.originalMode,
        bypassExpiresAt: block.bypassExpiresAt || state.block?.bypassExpiresAt,
      };
      state.lastCheckedAt = new Date().toISOString();
      state.source = 'api-503';
      state.error = null;
    },
    clearOperationBlock: (state) => {
      state.status = 'clear';
      state.block = null;
      state.lastCheckedAt = new Date().toISOString();
      state.source = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAppConfig.pending, (state) => {
        state.status = state.status === 'blocked' ? 'blocked' : 'checking';
        state.error = null;
      })
      .addCase(fetchAppConfig.fulfilled, (state, action) => {
        const block = action.payload;
        state.lastCheckedAt = new Date().toISOString();
        state.source = 'app-config';
        state.error = null;
        if (block?.active) {
          state.status = 'blocked';
          state.block = block;
          return;
        }
        state.status = 'clear';
        state.block = null;
      })
      .addCase(fetchAppConfig.rejected, (state, action) => {
        state.status = state.status === 'blocked' ? 'blocked' : 'unknown';
        state.error = action.payload || 'Unable to load operation status';
      });
  },
});

export const { setOperationBlockFromApiError, clearOperationBlock } = operationBlockSlice.actions;
export default operationBlockSlice.reducer;
